import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Plug, UserCircle2, KeyRound, Building2, Save, Globe, Mail, Coins } from "lucide-react";
import Billing from "../Billing";
import Integrations from "../Integrations";
import PublicHubSettings from "../settings/PublicHubSettings";
import EmailSettings from "../settings/EmailSettings";
import PaymentSettings from "../settings/PaymentSettings";
import { SectionHeader } from "./Capture";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { toast } from "sonner";

function Account() {
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [wsName, setWsName] = useState("");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    api.get("/account/workspace").then((r) => {
      setWs(r.data);
      setWsName(r.data?.name || "");
    });
  }, []);

  const saveWorkspace = async (e) => {
    e.preventDefault();
    if (!wsName.trim() || wsName === ws?.name) return;
    setBusy(true);
    try {
      const r = await api.patch("/account/workspace", { name: wsName.trim() });
      setWs(r.data);
      toast.success("Workspace renamed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rename");
    }
    setBusy(false);
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 8) { toast.error("New password must be 8+ chars"); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords don't match"); return; }
    setPwdBusy(true);
    try {
      await api.post("/account/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    }
    setPwdBusy(false);
  };

  const isOAuth = user?.auth_provider === "google";

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="settings-account">
      <div className="max-w-2xl space-y-6">
        {/* Profile */}
        <div className="surface p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-2xl ashra-gradient-bg grid place-items-center text-white font-bold text-lg shadow-sm">{user?.name?.[0]?.toUpperCase() || "A"}</div>
            <div>
              <p className="font-display text-lg font-bold text-ink">{user?.name}</p>
              <p className="text-sm text-meta">{user?.email}</p>
            </div>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-meta">Workspace ID</dt><dd className="font-mono text-ink-soft text-xs">{user?.workspace_id}</dd></div>
            <div className="flex justify-between"><dt className="text-meta">Plan</dt><dd className="capitalize"><span className="pill pill-ai">{user?.plan}</span></dd></div>
            <div className="flex justify-between"><dt className="text-meta">Auth provider</dt><dd className="capitalize text-ink-soft">{user?.auth_provider}</dd></div>
          </dl>
        </div>

        {/* Workspace rename */}
        <form onSubmit={saveWorkspace} className="surface p-6" data-testid="workspace-rename-form">
          <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-ai" /> Workspace name</h3>
          <p className="text-sm text-meta mb-4">Shown in the sidebar and billing receipts.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="workspace-name-input"
              value={wsName}
              onChange={(e) => setWsName(e.target.value)}
              placeholder="e.g. Ashra Creators"
              className="flex-1 rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <button
              type="submit"
              disabled={busy || !wsName.trim() || wsName === ws?.name}
              data-testid="save-workspace-btn"
              className="rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50 transition focus-ring"
            >
              <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>

        {/* Password change */}
        <form onSubmit={changePwd} className="surface p-6" data-testid="password-change-form">
          <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2 mb-1"><KeyRound className="h-4 w-4 text-ai" /> Change password</h3>
          <p className="text-sm text-meta mb-4">{isOAuth ? "You signed in with Google. Manage your password in your Google account." : "Use 8+ characters with a mix of letters and numbers."}</p>
          {!isOAuth && (
            <div className="space-y-2">
              <input
                type="password"
                placeholder="Current password"
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                className="w-full rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="current-password-input"
                required
              />
              <input
                type="password"
                placeholder="New password (8+ chars)"
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                className="w-full rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="new-password-input"
                required
                minLength={8}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="w-full rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="confirm-password-input"
                required
              />
              <button
                type="submit"
                disabled={pwdBusy}
                data-testid="save-password-btn"
                className="mt-2 rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50 transition focus-ring"
              >
                <KeyRound className="h-3.5 w-3.5" /> {pwdBusy ? "Updating…" : "Update password"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

const TABS = [
  { id: "billing", label: "Billing", icon: CreditCard, comp: Billing, hint: "Plans · invoices · usage" },
  { id: "payments", label: "Payments", icon: Coins, comp: PaymentSettings, hint: "Currency · provider · checkout behaviour" },
  { id: "hub", label: "Public Hub", icon: Globe, comp: PublicHubSettings, hint: "Handle · tagline · theme · CTA" },
  { id: "email", label: "Email", icon: Mail, comp: EmailSettings, hint: "Provider · templates · audit log" },
  { id: "integrations", label: "Integrations", icon: Plug, comp: Integrations, hint: "Instagram · WhatsApp · OAuth" },
  { id: "account", label: "Account", icon: UserCircle2, comp: Account, hint: "Profile · password · workspace" },
];

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "billing";
  const [tab, setTab] = useState(initial);
  const Active = TABS.find(t => t.id === tab)?.comp || Billing;
  const meta = TABS.find(t => t.id === tab);

  return (
    <div data-testid="settings-page">
      <SectionHeader title="Settings" subtitle="Configure your workspace" tabs={TABS} active={tab}
        onChange={(id) => { setTab(id); setParams({ tab: id }); }} hint={meta?.hint} />
      <Active />
    </div>
  );
}
